import type { Disposable } from '../core/Event';
import { DefaultRandom, type Random } from '../core/Random';
import { PickEmModel } from '../model/pickem/PickEmModel';
import { PlayerProfile } from '../model/profile/PlayerProfile';
import { DEFAULT_THEME, type Theme } from '../theme';
import { PickEmGrid } from '../widgets/pickem/PickEmGrid';
import { PickEmHudComponent } from './components/PickEmHudComponent';
import { DEFAULT_PICK_EM_PRIZES } from './fixtures/pickEmFixtures';
import { SceneBase } from './SceneBase';
import type { SceneLayoutContext } from './SceneComponent';

const FALLBACK_BALANCE = 0;

/**
 * Pick-Em demo — Phase F bonus-game foundation. 9 hidden boxes; tap 3 to
 * reveal coin prizes. The session starts at 0 balance and accumulates
 * through reveals; PLAY AGAIN reshuffles for another round.
 *
 * <p>Standalone for the demo selector. The MMS-shaped wiring will trigger
 * Pick-Em as a bonus from the slot's scatter trigger or the board's
 * bonus tile, replacing the standalone scene with an overlaid Fragment.
 */
export class PickEmDemoScene extends SceneBase {
  private grid: PickEmGrid | null = null;
  private hud: PickEmHudComponent | null = null;
  private model: PickEmModel | null = null;
  private readonly random: Random;
  private replaying = false;
  private readonly profile: PlayerProfile | null;
  private completionSubscription: Disposable | null = null;

  constructor(
    container: HTMLElement,
    theme: Theme = DEFAULT_THEME,
    profile: PlayerProfile | null = null,
  ) {
    super(container, { theme });
    this.random = new DefaultRandom();
    this.profile = profile;
  }

  protected buildContent(): void {
    this.startNewRound();
  }

  protected onResize(): void {
    this.layout();
  }

  protected disposeResources(wasInitialized: boolean): void {
    this.completionSubscription?.dispose();
    this.completionSubscription = null;
    this.model?.dispose();
    this.model = null;
    if (!wasInitialized) return;
    this.hud?.destroy();
    this.hud = null;
    if (this.grid) {
      this.grid.destroyGrid();
      this.grid.destroy({ children: true });
      this.grid = null;
    }
  }

  private startNewRound(): void {
    // Tear down any previous round's widgets.
    this.completionSubscription?.dispose();
    this.completionSubscription = null;
    this.model?.dispose();
    this.hud?.destroy();
    this.grid?.destroyGrid();
    this.grid?.destroy({ children: true });
    this.replaying = false;

    this.model = new PickEmModel(
      {
        boxCount: 9,
        picksAllowed: 3,
        prizes: DEFAULT_PICK_EM_PRIZES,
        columnCount: 3,
      },
      this.random,
    );

    this.grid = new PickEmGrid({
      boxCount: this.model.boxCount,
      columnCount: this.model.columnCount,
      accentColor: this.theme.palette.accent,
      defaultRevealColor: this.theme.palette.warm,
      onPick: (index) => void this.handlePick(index),
    });
    this.app.stage.addChild(this.grid);

    // HUD initial balance comes from the shared player profile when one
    // is available; otherwise the round starts at zero (round-local total).
    const initialBalance = this.profile?.balance ?? FALLBACK_BALANCE;
    this.hud = new PickEmHudComponent(
      this.model,
      { onReplay: () => void this.handleReplay() },
      initialBalance,
    );
    this.app.stage.addChild(this.hud);

    // Credit round winnings to the profile when the round completes.
    if (this.profile) {
      this.completionSubscription = this.model.events.on('pick-em-complete', (event) => {
        this.profile?.credit(event.totalWinnings);
      });
    }

    this.layout();
  }

  private layout(): void {
    if (!this.grid || !this.hud) return;
    const { width, height } = this.app.renderer;
    const naturalWidth = this.grid.totalWidth();
    const naturalHeight = this.grid.totalHeight();
    const horizontalGutter = 24;
    const scale = Math.min(1, (width - horizontalGutter * 2) / naturalWidth);
    this.grid.scale.set(scale);
    const scaledWidth = naturalWidth * scale;
    const scaledHeight = naturalHeight * scale;
    const gridX = Math.round((width - scaledWidth) / 2);
    const gridY = Math.round((height - scaledHeight) / 2 - 56 + 16);
    this.grid.x = gridX;
    this.grid.y = gridY;

    const ctx: SceneLayoutContext = {
      width,
      height,
      centerX: Math.round(width / 2),
      machineTop: gridY,
      machineBottom: gridY + scaledHeight,
      spinButtonY: Math.round(gridY + scaledHeight + 70),
    };
    this.hud.layout(ctx);
  }

  private async handlePick(index: number): Promise<void> {
    if (!this.model || !this.grid) return;
    const prize = this.model.pick(index);
    if (!prize) return;
    await this.grid.reveal(index, prize);
  }

  private handleReplay(): void {
    if (this.replaying) return;
    this.replaying = true;
    this.startNewRound();
  }
}
